import * as L from "leaflet";
import PropTypes from "prop-types";
import React, { useEffect, useContext, useState, useRef } from "react";
import { Grid, Link } from "@material-ui/core";
import MapIcon from "@material-ui/icons/Map";
import { Map, TileLayer, ZoomControl, FeatureGroup, withLeaflet } from "react-leaflet";

import { GeofencesContext } from "../../context";
import { GeofencesHollowLayers, GeofencesEditModes, MaxMapZoom } from "../../constants";
import { leafletURL, googleSatelliteURL } from "../../services/map.service";
import { debounce } from "../../services/misc.service";
import { CustomLeafletControl } from "../../services/map-control.service";
import { useMapEditor } from "../../hooks/useMapEditor";
import GeofenceLayer from "./GeofenceLayer";

const ANIMATE_MAP_ZOOMING = false;
const DEFAULT_PADDING = 10;
const TOP_PADDING = 240;
const LEFT_PADDING_RATIO = 0.24;

// The bounds object may be invalid, in this case we need to fallback to defaults.
const getGroupBounds = (group) => {
  const hasLayers = group && (!group.getLayers || group.getLayers().length > 0);
  const bounds = hasLayers ? group.getBounds() : new L.LatLngBounds();

  return bounds.isValid() ? bounds : null;
};

const TilesSwitch = withLeaflet(CustomLeafletControl);

const GeofencesMap = ({ defaultBounds, sidewalksMode, fullScreenMode }) => {
  const { sidewalksEditMode, layers, selectedLayer, selectedZone, addZone, updateZone } =
    useContext(GeofencesContext);

  const visibleLayers = Object.values(layers || {})
    .filter((layer) => !layer.hidden)
    .sort((l1, l2) => {
      if (GeofencesHollowLayers.includes(l1.type)) return -1;
      if (GeofencesHollowLayers.includes(l2.type)) return 1;

      return 0;
    });

  const isHexEditMode =
    selectedZone.editMode === GeofencesEditModes.hex ||
    selectedLayer.editMode === GeofencesEditModes.hex ||
    sidewalksEditMode === GeofencesEditModes.hex ||
    sidewalksEditMode === GeofencesEditModes.hexBrush;

  const {
    mapRef,
    rootRef,
    layersRef,
    polygonsRef,
    groupPolygonsRef,
    isMapReady,
    isRootReady,
    map,
    mapContainer,
    leafletRoot,
    leafletLayers,
    leafletGroups,
    mapOptions,
  } = useMapEditor({
    layers,
    selectedLayer,
    selectedZone,
    addZone,
    updateZone,
    sidewalksEditMode: sidewalksMode ? sidewalksEditMode : null,
  });

  const layerTilesRef = useRef(null);
  const [mapSettings, setMapSettings] = useState({ zoom: 0, bounds: defaultBounds });
  const [isSatellite, setIsSatellite] = useState(false);

  const topPadding = selectedZone.key || selectedLayer.key ? TOP_PADDING : DEFAULT_PADDING;
  const leftPadding = isMapReady ? mapContainer.clientWidth * LEFT_PADDING_RATIO : DEFAULT_PADDING;

  const boundsOptions = {
    paddingTopLeft: [leftPadding, topPadding],
    paddingBottomRight: [DEFAULT_PADDING, DEFAULT_PADDING],
  };

  const defaultBoundsOptions = isMapReady ? undefined : { bounds: defaultBounds, boundsOptions };

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.leafletElement.invalidateSize();
    }
  }, [fullScreenMode]);

  useEffect(() => {
    if (!isMapReady || !isRootReady) return;

    const bounds = getGroupBounds(leafletRoot) || defaultBounds;

    map.flyToBounds(bounds, boundsOptions);
  }, [visibleLayers.length > 0]);

  useEffect(() => {
    if (
      !isMapReady ||
      !isRootReady ||
      (!selectedZone.key && !selectedLayer.key) ||
      (selectedLayer.key && !Object.keys(layers[selectedLayer.key].zones).length)
    )
      return;

    let bounds;

    if (selectedZone.key) {
      bounds = getGroupBounds(leafletGroups[selectedZone.key]);
    } else if (selectedLayer.key) {
      bounds = getGroupBounds(leafletLayers[selectedLayer.key]);
    }

    if (bounds) {
      map.flyToBounds(bounds, boundsOptions);
    }
  }, [selectedLayer.key, selectedZone.key]);

  useEffect(() => {
    if (layerTilesRef.current) {
      layerTilesRef.current.leafletElement.setUrl(isSatellite ? googleSatelliteURL : leafletURL);
    }
  }, [isSatellite]);

  const onBoundsChanged = debounce(({ target }) => {
    setMapSettings({
      zoom: target.getZoom(),
      bounds: target.getBounds(),
    });
  });

  return (
    <Map
      ref={mapRef}
      editable
      doubleClickZoom
      scrollWheelZoom
      dragging
      keyboard
      animate
      touchZoom
      zoomControl={false}
      useFlyTo={ANIMATE_MAP_ZOOMING}
      onzoomend={onBoundsChanged}
      onmoveend={onBoundsChanged}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        cursor: isHexEditMode ? "pointer" : "",
      }}
      {...defaultBoundsOptions}
      {...mapOptions}
    >
      <TileLayer
        ref={layerTilesRef}
        url={leafletURL}
        attribution={`&copy; Superpedestrian ${new Date().getFullYear()}`}
        maxZoom={MaxMapZoom}
        maxNativeZoom={MaxMapZoom}
      />
      <ZoomControl position="bottomright" />
      <TilesSwitch position="bottomright">
        <Grid className="leaflet-bar">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            to="#"
            role="button"
            onClick={() => setIsSatellite(!isSatellite)}
            style={{
              minWidth: "unset",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <MapIcon fontSize="small" />
          </Link>
        </Grid>
      </TilesSwitch>
      <FeatureGroup ref={rootRef}>
        {visibleLayers.map((layer) => (
          <GeofenceLayer
            key={layer.key}
            mapSettings={mapSettings}
            layer={layer}
            layersRef={layersRef}
            polygonsRef={polygonsRef}
            groupPolygonsRef={groupPolygonsRef}
            sidewalksMode={sidewalksMode}
          />
        ))}
      </FeatureGroup>
    </Map>
  );
};

GeofencesMap.propTypes = {
  defaultBounds: L.LatLngBounds,
  sidewalksMode: PropTypes.bool,
  fullScreenMode: PropTypes.bool,
};

GeofencesMap.defaultProps = {
  defaultBounds: L.latLngBounds([
    [42.35, -71.11],
    [42.37, -71.06],
  ]),
  sidewalksMode: false,
  fullScreenMode: false,
};

export default GeofencesMap;
